import { useEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    target: '[data-testid="onboarding-form"]',
    overline: "Step 1 · Your story",
    title: "Fill in your profile",
    body: "Business name, bios, services, your reader offer — this is the raw material our editorial desk turns into your magazine feature. The more you give, the better the result.",
  },
  {
    target: '[data-testid="photos-section"]',
    overline: "Step 2 · Your photos",
    title: "Upload 3–5 photos of you",
    body: "Clear shots, different angles. Our studio tunes them into magazine-grade portraits that still look unmistakably you.",
  },
  {
    target: '[data-testid="ref-docs-section"]',
    overline: "Step 3 · Your material",
    title: "Add documents & your interview",
    body: "Brochures, menus, price lists — plus your rapid-fire 5 Questions further down. Then relax: the desk takes it from there.",
  },
];

export const PortalTour = ({ userKey }) => {
  const storageKey = `glossy_tour_${userKey}`;
  const [step, setStep] = useState(-1);

  useEffect(() => {
    if (localStorage.getItem(storageKey)) return;
    const t = setTimeout(() => setStep(0), 1200);
    return () => clearTimeout(t);
  }, [storageKey]);

  useEffect(() => {
    if (step < 0) return;
    const el = document.querySelector(STEPS[step].target);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("tour-highlight");
    return () => el.classList.remove("tour-highlight");
  }, [step]);

  const done = () => { localStorage.setItem(storageKey, "1"); setStep(-1); };
  if (step < 0) return null;
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div className="fixed bottom-5 right-5 z-[90] w-[min(92vw,400px)] bg-ink text-cream border border-gloss-gold/50 shadow-2xl p-7 animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="portal-tour">
      <button onClick={done} className="absolute top-4 right-4 text-cream/50 hover:text-cream transition-colors" data-testid="tour-skip" aria-label="Skip tour">
        <X size={16} />
      </button>
      <p className="overline text-gloss-gold mb-3">{s.overline}</p>
      <p className="font-serif text-2xl leading-tight">{s.title}</p>
      <p className="font-sans text-sm text-cream/70 mt-3 leading-relaxed">{s.body}</p>
      <div className="flex items-center justify-between mt-6">
        <span className="flex gap-2">
          {STEPS.map((_, i) => (
            <span key={i} className={`w-2 h-2 ${i === step ? "bg-gloss-gold" : "bg-cream/25"}`} />
          ))}
        </span>
        <span className="flex items-center gap-3">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="overline text-cream/60 hover:text-cream inline-flex items-center gap-1.5 transition-colors" data-testid="tour-back">
              <ArrowLeft size={13} /> Back
            </button>
          )}
          <button
            onClick={() => (last ? done() : setStep(step + 1))}
            className="bg-gloss-gold text-ink px-5 py-2.5 overline inline-flex items-center gap-2 hover:bg-cream transition-colors"
            data-testid="tour-next"
          >
            {last ? "Let's begin" : "Next"} {!last && <ArrowRight size={13} />}
          </button>
        </span>
      </div>
    </div>
  );
};
